import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import SharePanel from './components/SharePanel';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useFavorites } from '@/hooks/useFavorites';
import { formatDate, formatViews, getArticleById, getRelatedArticles } from '@/utils/articles';

export default function DetailPage() {
  const { id = '' } = useParams();
  const article = getArticleById(id);
  const { isFavorite, toggleFavorite } = useFavorites();

  useDocumentTitle({
    title: article ? `${article.title} - 91黑料网` : '内容不存在 - 91黑料网',
    description: article ? article.excerpt : '该爆料内容不存在或已被删除。',
    keywords: article
      ? `${article.tags.join(',')},91黑料网,${article.category}`
      : '91黑料网',
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
          <EmptyState
            title="内容不存在或已被删除"
            description="你访问的爆料可能已经下线，返回首页看看其他最新内容吧。"
            primaryLabel="返回首页"
          />
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedArticles(article, 3);
  const favorited = isFavorite(article.id);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="mx-auto max-w-[1280px] px-4 py-6 md:px-6">
        <Breadcrumb
          items={[
            { label: article.category, to: `/category/${article.categorySlug}` },
            { label: '爆料详情' },
          ]}
        />

        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <article className="animate-fade-up lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="flex cursor-pointer items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-700"
                >
                  <i className="ri-price-tag-3-line"></i>
                  {article.category}
                </Link>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/tags/${encodeURIComponent(tag)}`}
                    className="cursor-pointer rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <h1 className="mt-4 text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
                {article.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-newspaper-line text-primary-500"></i>
                  来源：{article.source}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-primary-500"></i>
                  发布于 {formatDate(article.publishTime)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line text-primary-500"></i>
                  {formatViews(article.views)} 次浏览
                </span>
              </div>

              <div className="relative mt-5 h-[220px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[380px]">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 91黑料网`}
                  className="h-full w-full object-top"
                />
              </div>

              <div className="mt-6 space-y-4">
                {article.content.map((para, idx) => (
                  <p key={idx} className="text-base leading-loose text-foreground-800">
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4 text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-line mr-1 text-primary-500"></i>
                免责声明：本文内容基于网络公开信息整理与模拟演示，不代表本站立场，请读者理性看待并自行核实。
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(article.id)}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                    favorited
                      ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                      : 'border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={favorited ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
                  {favorited ? '已收藏' : '收藏本篇'}
                </button>
                <SharePanel article={article} />
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-folder-line"></i>
                  进入「{article.category}」频道
                </Link>
              </div>
            </div>
          </article>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 lg:sticky lg:top-28">
              <h3 className="mb-3 flex items-center gap-2 border-b border-background-200 pb-2 text-base font-black text-foreground-950">
                <i className="ri-link-m text-accent-500"></i>
                相关推荐
              </h3>
              {related.length > 0 ? (
                <ul className="flex flex-col gap-3">
                  {related.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/detail/${item.id}`}
                        className="group flex cursor-pointer gap-3"
                      >
                        <span className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                          <img
                            src={item.cover}
                            alt={item.title}
                            title={`${item.title} - 91黑料网`}
                            className="h-full w-full object-top"
                          />
                        </span>
                        <span className="min-w-0">
                          <span className="clamp-2 block text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </span>
                          <span className="mt-1 block text-xs text-foreground-400">
                            {formatDate(item.publishTime).slice(0, 10)}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="py-4 text-center text-xs text-foreground-400">暂无相关推荐</p>
              )}
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}